import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { finalize, firstValueFrom, map, Observable, Subject, takeUntil } from 'rxjs';
import { DepositData, GetDepositRequestParams } from './deposits.interface';
import { Router } from '@angular/router';
import { DepositsService } from '../../services/deposits/deposits.service';
import { getOffset, getUtcDateRangeForLocalDate, getYesterday, showNotification } from 'src/app/shared/helpers/helpers';
import { ModalContent } from '../../shared/modal-content';
import { ModalComponent } from 'angular-custom-modal';
import { OptionItem } from '../../shared/types/api-responses.type';
import { MerchantsService } from '../../services/merchants/merchants.service';
import { ProvidersService } from '../../services/providers/providers.service';
import { CategoriesService } from '../../services/categories/categories.service';
import { FlatpickrOptions } from 'ng2-flatpickr';
import { UserData } from '../../interfaces/users.interface';
import { Store } from '@ngrx/store';
import { selectCurrentUser } from '../../services/users/user.selectors';

@Component({
  selector: 'app-deposits',
  templateUrl: './deposits.component.html',
  styleUrls: ['./deposits.component.css'],
})
export class DepositsComponent implements OnInit, OnDestroy {
  @ViewChild('modal') modal!: ModalComponent;

  private destroy$ = new Subject<void>();
  loading = false;
  merchants: OptionItem[] = [];
  providers: OptionItem[] = [];
  categories: OptionItem[] = [];

  @ViewChild('createdFromPicker') createdFromPicker: any;
  createdFromConfig: FlatpickrOptions = {
    altInput: true,
    altFormat: 'd-m-Y',
    dateFormat: 'Y-m-d',
  };

  @ViewChild('createdToPicker') createdToPicker: any;
  createdToConfig: FlatpickrOptions = {
    altInput: true,
    altFormat: 'd-m-Y', // ✅ Ditampilkan sebagai DD-MM-YYYY
    dateFormat: 'Y-m-d', // ✅ Disimpan dalam format backend-friendly
    minDate: '', // will be set dynamically
  };

  status: OptionItem[] = [
    { label: 'Inquiry', value: '3' },
    { label: 'Pending', value: '1' },
    { label: 'Failed', value: '2' },
    { label: 'Success', value: '0' },
    { label: 'Reversal', value: '4' },
  ];
  types: OptionItem[] = [
    { label: 'Deposit', value: '1' },
    { label: 'Deposit', value: '2' },
  ];

  modalData: ModalContent = new ModalContent({
    header: 'Confirmation',
    body: 'Are you sure you want to delete this deposit?',
    showCloseButton: true,
    showConfirmButton: true,
    closeButtonLabel: 'Cancel',
    confirmButtonLabel: 'Delete',
    closeButtonClass: 'btn-secondary',
    confirmButtonClass: 'btn-danger',
  });

  selectedId = '';

  params = new GetDepositRequestParams();
  totalRows = 0;

  paramsExcel = new GetDepositRequestParams();
  totalRowsExcel = 0;

  cols = [
    { field: 'no', title: 'No' },
    { field: 'tx_id', title: 'Tx ID' },
    { field: 'merchant_name', title: 'Merchant Name' },
    { field: 'status_name', title: 'Status' },
    { field: 'amount', title: 'Amount' },
    { field: 'first_balance', title: 'First Balance' },
    { field: 'last_balance', title: 'Last Balance' },
    { field: 'created_at', title: 'Created Date' },
    { field: 'action', title: 'Action', type: 'action' },
  ];

  pageSizeOptions = [5, 10, 25, 50, 100];
  rows: DepositData[] = [];
  rowsExcel: DepositData[] = [];

  userAuth$!: Observable<UserData | null>;
  isAdmin$!: Observable<boolean>;

  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private depositService: DepositsService,
    private readonly merchantService: MerchantsService,
    private readonly providerService: ProvidersService, // private readonly roleService: RolesService,
    private readonly categoryService: CategoriesService,
    public storeData: Store<any>
  ) {}

  ngOnInit(): void {
    const yesterday = getYesterday();
    this.createdFromConfig = {
      altInput: true,
      altFormat: 'd-m-Y',
      dateFormat: 'Y-m-d',
      defaultDate: yesterday,
    };

    this.createdToConfig = {
      altInput: true,
      altFormat: 'd-m-Y',
      dateFormat: 'Y-m-d',
      defaultDate: yesterday,
    };

    const { startUtc, endUtc } = getUtcDateRangeForLocalDate(yesterday);
    this.params.createdFrom = startUtc;
    this.params.createdTo = endUtc;

    this.userAuth$ = this.storeData.select(selectCurrentUser);
    this.isAdmin$ = this.userAuth$.pipe(map((user) => user?.roles?.some((role) => role.code === 'admin') ?? false));

    this.userAuth$.subscribe((user) => {
      if (user) {
        this.isAdmin$.pipe(takeUntil(this.destroy$)).subscribe((isAdmin) => {
          if (isAdmin) {
            this.getMerchantData().then();
            this.getProviderData().then();
          } else {
            this.params.merchantCode = user.merchant.code;
            this.getCategoryData().then();
          }

          this.getDeposits().then();
        });
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async getDeposits(): Promise<void> {
    this.loading = true;
    this.rows = [];
    this.rowsExcel = [];

    try {
      this.depositService
        .getDepositService(this.params)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.loading = false))
        )
        .subscribe({
          next: (response) => {
            this.rows = this.processDataForTable(response.data);
            this.totalRows = response.totalPage;
          },
          error: (err) => console.error('Error fetching deposits data:', err),
        });
    } catch (error) {
      showNotification('error', 'Failed to fetch deposits', JSON.stringify(error));
    } finally {
      this.loading = false;
    }

    // get list for excel
    this.paramsExcel.rowPerPage = 999999999999999999;
    try {
      this.depositService
        .getDepositService(this.paramsExcel)
        .pipe(
          takeUntil(this.destroy$),
          finalize(() => (this.loading = false))
        )
        .subscribe({
          next: (response) => {
            this.rowsExcel = this.processDataForTable(response.data);
            this.totalRowsExcel = response.totalPage;
          },
          error: (err) => console.error('Error fetching deposits data for excel:', err),
        });
    } catch (error) {
      showNotification('error', 'Failed to fetch deposits for excel', JSON.stringify(error));
    } finally {
      // this.loading = false;
    }
  }

  private async getMerchantData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.merchantService.getMerchants());
      this.merchants = data.map((merchant) => ({ label: merchant.name, value: merchant.code }));
    } catch (error) {
      showNotification('error', 'Failed to fetch merchants', JSON.stringify(error));
    }
  }

  private async getProviderData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.providerService.getProviders());
      this.providers = data.map((provider) => ({ label: provider.name, value: provider.code }));
      console.log('providerOption: ', this.providers);
    } catch (error) {
      showNotification('error', 'Failed to fetch provider', JSON.stringify(error));
    }
  }

  private async getCategoryData(): Promise<void> {
    try {
      const { data } = await firstValueFrom(this.categoryService.getCategories());
      this.categories = data.map((category) => ({ label: category.name, value: category.code }));
      console.log('categoryOption: ', this.categories);
    } catch (error) {
      showNotification('error', 'Failed to fetch category', JSON.stringify(error));
    }
  }

  processDataForTable(data: DepositData[]) {
    const formattedData = data.map((each, i) => ({
      ...each,
      no: getOffset(this.params.pageNo, this.params.rowPerPage) + i + 1,
    }));

    console.log('Processed Data:', formattedData); // 🔍 Debug output

    return formattedData;
  }

  changeServer(data: any): void {
    this.params.pageNo = data.current_page;
    this.params.rowPerPage = data.pagesize;
    this.params.filter = data.search;
    this.getDeposits();
  }

  onInputChanged(value: string): void {
    console.log('Debounced Input:', value);
    this.params.filter = '';
    if (value) {
      this.params.filter = value;
    }
    this.cdRef.detectChanges();
  }

  goToForm(id: string): void {
    if (id === 'new') {
      this.router.navigate(['/deposits/new']).then();
      return;
    }
    this.router.navigate(['/deposits/edit', id]).then();
    return;
  }

  // private async getRoleData(): Promise<void> {
  //   try {
  //     const { data } = await firstValueFrom(this.roleService.getRoles());
  //     this.roles = data.map(role => ({ label: role.name, value: role.id }));
  //   } catch (error) {
  //     showNotification('error', 'Failed to fetch roles', JSON.stringify(error));
  //   }
  // }

  async deleteDeposit(id: string): Promise<void> {
    this.loading = true;

    try {
      const delResp = await firstValueFrom(this.depositService.deleteDepositById(id));
      showNotification('success', 'Success to delete deposit', JSON.stringify(delResp));
      this.getDeposits();
    } catch (error) {
      console.error('Delete Deposit Error:', error);
    } finally {
      this.loading = false;
    }
  }

  async onModalDeleteConfirm(id: string): Promise<void> {
    await this.deleteDeposit(id);
    this.modal.close();
  }

  onMerchantChange(merchantOption: OptionItem): void {
    this.params.merchantCode = null;
    if (merchantOption) {
      console.log('Merchant Change:', merchantOption.value);
      console.log('Merchant Change:', merchantOption.label);
      this.params.merchantCode = merchantOption.value;
    }
    this.getDeposits();
  }

  onProviderChange(providerOption: OptionItem): void {
    this.params.providerCode = null;
    if (providerOption) {
      console.log('Provider Change:', providerOption.value);
      console.log('Provider Change:', providerOption.label);
      this.params.providerCode = providerOption.value;
    }
    this.getDeposits();
  }

  onCategoryChange(categoryOption: OptionItem): void {
    this.params.categoryCode = null;
    if (categoryOption) {
      console.log('Category Change:', categoryOption.value);
      console.log('Category Change:', categoryOption.label);
      this.params.categoryCode = categoryOption.value;
    }
    this.getDeposits();
  }

  onStatusChange(statusOption: OptionItem): void {
    this.params.status = null;
    if (statusOption) {
      console.log('status Change:', statusOption.value);
      console.log('status Change:', statusOption.label);
      this.params.status = statusOption.value;
    }
    this.getDeposits();
  }

  onCreatedFromChange(event: any): void {
    this.params.createdFrom = null;

    const selectedCreatedFromDate = event?.target ? event.target.value : event;
    console.log('Selected Created From date:', selectedCreatedFromDate);

    if (!selectedCreatedFromDate) return;

    const selectedDate = new Date(selectedCreatedFromDate);
    const formattedDate = selectedDate.toISOString().split('T')[0];
    console.log('Formatted Created From Date:', formattedDate);

    // Calculate minDate (1 month before) and maxDate (1 month after)
    const minDate = new Date(selectedDate);
    minDate.setMonth(minDate.getMonth() - 1);

    const maxDate = new Date(selectedDate);
    maxDate.setMonth(maxDate.getMonth() + 1);

    // Set min and max for the createdToPicker
    this.createdToPicker.flatpickr.set('minDate', minDate);
    this.createdToPicker.flatpickr.set('maxDate', maxDate);
    this.createdToPicker.flatpickr.set('disable', [(date: Date) => date < minDate || date > maxDate]);

    console.log('Allowed createdTo range:', minDate, 'to', maxDate);
    this.params.createdFrom = selectedCreatedFromDate;

    // Call your logic
    this.getDeposits();
  }

  onCreatedToChange(event: any): void {
    this.params.createdTo = null;
    const selectedCreatedToDate = event?.target ? event.target.value : event;

    console.log('Selected Created To date:', selectedCreatedToDate);

    if (!selectedCreatedToDate) return;

    this.params.createdTo = selectedCreatedToDate;
    console.log('Selected this.params:', this.params);

    // Call your function to get deposits or any other logic you need
    this.getDeposits();
  }

  // onTypeChange(typeOption: OptionItem): void {
  //   this.params.type = null;
  //   if (typeOption) {
  //     console.log('Type Change:', typeOption.value);
  //     console.log('type Change:', typeOption.label);
  //     this.params.type = typeOption.value;
  //   }
  //   this.getDeposits();
  // }

  get isCompactFilter(): boolean {
    return this.merchants.length === 0;
  }
}
